import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, Calculator, Target, DollarSign, PieChart, BarChart3, Info } from 'lucide-react';

interface DataPoint {
  annee: number;
  sansVersement: number;
  avecVersement: number;
  capitalInvesti: number;
}

const SimulateurInvestissement: React.FC = () => {
  const [capitalInitial, setCapitalInitial] = useState<number>(10000);
  const [versementMensuel, setVersementMensuel] = useState<number>(200);
  const [dureeAnnees, setDureeAnnees] = useState<number>(10);
  const [avecVersementProgramme, setAvecVersementProgramme] = useState<boolean>(true);
  const [data, setData] = useState<DataPoint[]>([]);

  const tauxRendement = 0.04; // 4% par an

  const calculerProjection = () => {
    const nouvelleDonnee: DataPoint[] = [];
    
    for (let annee = 0; annee <= dureeAnnees; annee++) {
      // Sans versement programmé - capital initial seulement
      const valeurSansVersement = capitalInitial * Math.pow(1 + tauxRendement, annee);
      
      // Avec versement programmé
      let valeurAvecVersement = capitalInitial * Math.pow(1 + tauxRendement, annee);
      let capitalTotalInvesti = capitalInitial;
      
      if (avecVersementProgramme && annee > 0) {
        // Formule pour rente avec capitalisation
        const versementAnnuel = versementMensuel * 12;
        const valeurVersements = versementAnnuel * ((Math.pow(1 + tauxRendement, annee) - 1) / tauxRendement);
        valeurAvecVersement += valeurVersements;
        capitalTotalInvesti += versementAnnuel * annee;
      }
      
      nouvelleDonnee.push({
        annee,
        sansVersement: Math.round(valeurSansVersement),
        avecVersement: Math.round(valeurAvecVersement),
        capitalInvesti: Math.round(capitalTotalInvesti)
      });
    }
    
    setData(nouvelleDonnee);
  };

  useEffect(() => {
    calculerProjection();
  }, [capitalInitial, versementMensuel, dureeAnnees, avecVersementProgramme]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const resultatsFinaux = data[data.length - 1];
  const gainSansVersement = resultatsFinaux ? resultatsFinaux.sansVersement - capitalInitial : 0;
  const gainAvecVersement = resultatsFinaux ? resultatsFinaux.avecVersement - resultatsFinaux.capitalInvesti : 0;
  const totalInvesti = resultatsFinaux ? resultatsFinaux.capitalInvesti : capitalInitial;

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #fafbfc 0%, #f1f5f9 50%, #e2e8f0 100%)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center bg-white border border-gray-200 shadow-sm text-gray-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
            Simulateur d'Investissement Professionnel
          </div>
          <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4 tracking-tight">
            Calculateur d'{' '}
            <span style={{ 
              background: 'linear-gradient(135deg, #059669 0%, #0369a1 100%)',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              color: 'transparent'
            }}>
              Investissement
            </span>
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto font-light">
            Projections financières précises avec un rendement annuel de 4% et stratégie de versements programmés
          </p>
        </div>

        {/* Layout: Paramètres à gauche, Graphique à droite */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8 mb-8">
          {/* Colonne de gauche: Paramètres et Résultats */}
          <div className="xl:col-span-1 space-y-6">
            {/* Panel de contrôles */}
            <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100 backdrop-blur-sm">
              <div className="border-b border-gray-100 pb-6 mb-8">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                    <Calculator className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Paramètres de Simulation</h2>
                    <p className="text-sm text-gray-500 mt-1">Configurez votre stratégie d'investissement</p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                {/* Capital Initial */}
                <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-4 rounded-xl border border-emerald-100">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="w-6 h-6 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <DollarSign className="w-3 h-3 text-emerald-600" />
                    </div>
                    <label className="block text-sm font-semibold text-gray-800">
                      Capital Initial
                    </label>
                  </div>
                  <input
                    type="range"
                    min="1000"
                    max="100000"
                    step="1000"
                    value={capitalInitial}
                    onChange={(e) => setCapitalInitial(Number(e.target.value))}
                    className="w-full h-3 bg-emerald-100 rounded-lg appearance-none cursor-pointer slider-emerald"
                    style={{
                      background: `linear-gradient(to right, #059669 0%, #059669 ${((capitalInitial - 1000) / (100000 - 1000)) * 100}%, #d1fae5 ${((capitalInitial - 1000) / (100000 - 1000)) * 100}%, #d1fae5 100%)`
                    }}
                  />
                  <div className="mt-3 space-y-2">
                    <input
                      type="number"
                      value={capitalInitial}
                      onChange={(e) => setCapitalInitial(Number(e.target.value))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-center font-semibold text-sm bg-white shadow-sm"
                    />
                    <div className="text-center">
                      <div className="text-2xl font-bold text-emerald-600">{formatCurrency(capitalInitial)}</div>
                      <div className="text-xs text-gray-500">Montant de départ</div>
                    </div>
                  </div>
                </div>

                {/* Versement Mensuel */}
                <div className={`p-4 rounded-xl border transition-all duration-300 ${
                  avecVersementProgramme 
                    ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-100' 
                    : 'bg-gray-50 border-gray-200 opacity-60'
                }`}>
                  <div className="flex items-center space-x-2 mb-3">
                    <div className={`w-6 h-6 rounded-lg flex items-center justify-center ${
                      avecVersementProgramme ? 'bg-blue-100' : 'bg-gray-100'
                    }`}>
                      <TrendingUp className={`w-3 h-3 ${
                        avecVersementProgramme ? 'text-blue-600' : 'text-gray-400'
                      }`} />
                    </div>
                    <label className="block text-sm font-semibold text-gray-800">
                      Versement Mensuel
                    </label>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="2000"
                    step="50"
                    value={versementMensuel}
                    onChange={(e) => setVersementMensuel(Number(e.target.value))}
                    disabled={!avecVersementProgramme}
                    className="w-full h-3 bg-blue-100 rounded-lg appearance-none cursor-pointer slider-blue"
                    style={{
                      background: avecVersementProgramme 
                        ? `linear-gradient(to right, #0369a1 0%, #0369a1 ${(versementMensuel / 2000) * 100}%, #dbeafe ${(versementMensuel / 2000) * 100}%, #dbeafe 100%)`
                        : '#e5e7eb'
                    }}
                  />
                  <div className="mt-3 space-y-2">
                    <input
                      type="number"
                      value={versementMensuel}
                      onChange={(e) => setVersementMensuel(Number(e.target.value))}
                      disabled={!avecVersementProgramme}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center font-semibold text-sm bg-white shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
                    />
                    <div className="text-center">
                      <div className={`text-2xl font-bold ${
                        avecVersementProgramme ? 'text-blue-600' : 'text-gray-400'
                      }`}>{formatCurrency(versementMensuel)}</div>
                      <div className="text-xs text-gray-500">par mois</div>
                    </div>
                  </div>
                </div>

                {/* Durée */}
                <div className="bg-gradient-to-r from-purple-50 to-violet-50 p-4 rounded-xl border border-purple-100">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="w-6 h-6 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Target className="w-3 h-3 text-purple-600" />
                    </div>
                    <label className="block text-sm font-semibold text-gray-800">
                      Durée d'investissement
                    </label>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="30"
                    step="1"
                    value={dureeAnnees}
                    onChange={(e) => setDureeAnnees(Number(e.target.value))}
                    className="w-full h-3 bg-purple-100 rounded-lg appearance-none cursor-pointer slider-purple"
                    style={{
                      background: `linear-gradient(to right, #7c3aed 0%, #7c3aed ${((dureeAnnees - 1) / (30 - 1)) * 100}%, #e9d5ff ${((dureeAnnees - 1) / (30 - 1)) * 100}%, #e9d5ff 100%)`
                    }}
                  />
                  <div className="mt-3 space-y-2">
                    <input
                      type="number"
                      value={dureeAnnees}
                      onChange={(e) => setDureeAnnees(Number(e.target.value))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-center font-semibold text-sm bg-white shadow-sm"
                    />
                    <div className="text-center">
                      <div className="text-2xl font-bold text-purple-600">{dureeAnnees}</div>
                      <div className="text-xs text-gray-500">année{dureeAnnees > 1 ? 's' : ''}</div>
                    </div>
                  </div>
                </div>

                {/* Toggle Versement */}
                <div className="bg-gradient-to-r from-slate-50 to-gray-50 p-4 rounded-xl border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-6 h-6 bg-gray-100 rounded-lg flex items-center justify-center">
                        <PieChart className="w-3 h-3 text-gray-600" />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-gray-800">Versements programmés</div>
                        <div className="text-xs text-gray-500">Activer les apports mensuels réguliers</div>
                      </div>
                    </div>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={avecVersementProgramme}
                        onChange={(e) => setAvecVersementProgramme(e.target.checked)}
                        className="sr-only"
                      />
                      <div className={`relative inline-flex h-7 w-12 rounded-full transition-all duration-300 ease-in-out shadow-inner ${
                        avecVersementProgramme 
                          ? 'bg-gradient-to-r from-emerald-500 to-blue-500 shadow-emerald-200' 
                          : 'bg-gray-300 shadow-gray-200'
                      }`}>
                        <div className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-2 ring-white transition-all duration-300 ease-in-out ${
                          avecVersementProgramme ? 'translate-x-6 mt-1' : 'translate-x-1 mt-1'
                        }`} />
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* Colonne de droite: Résultats et Graphique */}
          <div className="xl:col-span-2 space-y-6">
            {/* Résultats en cartes */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Capital Investi */}
              <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100 relative overflow-hidden backdrop-blur-sm hover:shadow-2xl transition-all duration-300 group">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-400 to-emerald-500"></div>
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform duration-300">
                    <DollarSign className="w-5 h-5 text-white" />
                  </div>
                </div>
                <h3 className="text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">Capital Investi</h3>
                <p className="text-2xl font-bold text-gray-900 mb-1">
                  {formatCurrency(totalInvesti)}
                </p>
                <div className="text-xs text-emerald-600 font-medium bg-emerald-50 px-2 py-1 rounded-full inline-block">
                  Montant total
                </div>
              </div>

              {/* Valeur Sans Versement */}
              <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100 relative overflow-hidden backdrop-blur-sm hover:shadow-2xl transition-all duration-300 group">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-400 to-purple-500"></div>
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform duration-300">
                    <PieChart className="w-5 h-5 text-white" />
                  </div>
                </div>
                <h3 className="text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">Sans Versement</h3>
                <p className="text-2xl font-bold text-gray-900 mb-2">
                  {resultatsFinaux ? formatCurrency(resultatsFinaux.sansVersement) : '0€'}
                </p>
                <div className="flex items-center space-x-2">
                  <div className="text-xs text-green-600 font-bold bg-green-50 px-2 py-1 rounded-full">
                    +{formatCurrency(gainSansVersement)}
                  </div>
                  <div className="text-xs text-gray-500">gain</div>
                </div>
              </div>

              {/* Valeur Avec Versement */}
              {avecVersementProgramme && (
                <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100 relative overflow-hidden backdrop-blur-sm hover:shadow-2xl transition-all duration-300 group">
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-400 to-blue-500"></div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform duration-300">
                      <TrendingUp className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <h3 className="text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">Avec Versement</h3>
                  <p className="text-2xl font-bold text-gray-900 mb-2">
                    {resultatsFinaux ? formatCurrency(resultatsFinaux.avecVersement) : '0€'}
                  </p>
                  <div className="flex items-center space-x-2">
                    <div className="text-xs text-green-600 font-bold bg-green-50 px-2 py-1 rounded-full">
                      +{formatCurrency(gainAvecVersement)}
                    </div>
                    <div className="text-xs text-gray-500">gain</div>
                  </div>
                </div>
              )}

              {/* Rendement */}
              <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100 relative overflow-hidden backdrop-blur-sm hover:shadow-2xl transition-all duration-300 group">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-amber-400 to-orange-500"></div>
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform duration-300">
                    <Target className="w-5 h-5 text-white" />
                  </div>
                </div>
                <h3 className="text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">Rendement Annuel</h3>
                <p className="text-2xl font-bold text-gray-900 mb-2">4.00%</p>
                <div className="text-xs text-amber-600 font-medium bg-amber-50 px-2 py-1 rounded-full inline-block">
                  Taux fixe
                </div>
              </div>
            </div>

            {/* Graphique */}
            <div className="bg-white rounded-2xl p-8 shadow-2xl border border-gray-100 backdrop-blur-sm">
              <div className="border-b border-gray-100 pb-6 mb-8">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                    <BarChart3 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Évolution Patrimoniale</h2>
                    <p className="text-sm text-gray-500 mt-1">Projection sur {dureeAnnees} année{dureeAnnees > 1 ? 's' : ''} avec rendement de 4%</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-slate-50 to-blue-50 p-6 rounded-xl">
                <ResponsiveContainer width="100%" height={450}>
                <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis 
                    dataKey="annee" 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#64748b', fontSize: 12 }}
                    label={{ value: 'Années', position: 'insideBottom', offset: -10, style: { textAnchor: 'middle', fill: '#64748b' } }}
                  />
                  <YAxis 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#64748b', fontSize: 12 }}
                    tickFormatter={(value) => `${(value / 1000).toFixed(0)}k€`}
                    label={{ value: 'Valeur (€)', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#64748b' } }}
                  />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '0.5rem',
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                    }}
                    formatter={(value: any, name: string) => [
                      formatCurrency(value),
                      name === 'sansVersement' ? 'Sans versement' :
                      name === 'avecVersement' ? 'Avec versement' : 'Capital investi'
                    ]}
                    labelFormatter={(label) => `Année ${label}`}
                  />
                  <Legend 
                    wrapperStyle={{
                      paddingTop: '20px'
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="capitalInvesti"
                    stroke="#f59e0b"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    name="Capital investi"
                    dot={{ fill: '#f59e0b', strokeWidth: 2, r: 4 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="sansVersement"
                    stroke="#8b5cf6"
                    strokeWidth={3}
                    name="Sans versement"
                    dot={{ fill: '#8b5cf6', strokeWidth: 2, r: 4 }}
                  />
                  {avecVersementProgramme && (
                    <Line
                      type="monotone"
                      dataKey="avecVersement"
                      stroke="#14b8a6"
                      strokeWidth={3}
                      name="Avec versement"
                      dot={{ fill: '#14b8a6', strokeWidth: 2, r: 4 }}
                    />
                  )}
                </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        {/* Informations complémentaires */}
        <div className="bg-gradient-to-r from-slate-50 via-blue-50 to-indigo-50 rounded-2xl p-8 border border-slate-200 shadow-xl">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
              <Info className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Informations Importantes</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-gray-700">
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-sm">Rendement fixe garanti de <strong className="text-emerald-600">4% par an</strong></span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-sm">Capitalisation <strong className="text-blue-600">composée annuelle</strong></span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-sm">Versements <strong className="text-purple-600">intégrés</strong> dans les calculs</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-sm">Simulation <strong className="text-amber-600">indicative</strong> à titre informatif</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimulateurInvestissement;